import { Injectable } from '@angular/core';
import { FleetTrackerIcons } from './fleet-tracker-icons';
import { IconType } from './model/enum.model';

const RANK = 6; // image is a 6 X 6 icon matrix: one image per each 10 degrees of rotation

@Injectable()
export class FleetTrackerIconService {

    private _origin = undefined;
    private _chipAnchor = undefined;
    private _chipSize = undefined;
    private _chipBorderAnchor = undefined;
    private _chipBorderSize = undefined;

    private _crumbAnchor = undefined;
    private _crumbSize = undefined;

    private _rightPinAnchor = undefined;
    private _leftPinAnchor = undefined;
    private _pinAnchor = undefined;
    private _pinSize = undefined;

    private _smallPinSize = undefined;
    private _smallPinAnchor = undefined;

    private _labelAnchor = undefined;
    private _labelSize = undefined;

    private _riskLevelAnchor = undefined;
    private _riskLevelSize = undefined;

    private _trafficCamAnchor = undefined;
    private _trafficCamSize = undefined;

    private readonly _icons = new Map<IconType, google.maps.Icon[][]>();

    private readonly _iconMap = new FleetTrackerIcons();

    private _asset: google.maps.Icon[][];
    private _assetUnlabeled: google.maps.Icon[][];
    private _geofenceIcon: google.maps.Icon[][];
    private _geofenceIconUnlabled: google.maps.Icon[][];
    private _map: google.maps.Map;
    private pixelCoordinates: any;

    constructor() { }

    //
    // Invoke intialize once the google maps API is available.
    //
    initialize() {
        this.initializeMarkerPlacements();
        this.initializeIcons();
        // this.initializeDistractionIcons();

        this._icons.set(IconType.Stopped, this.indexedIcons(IconType.Stopped, 19));
        this._icons.set(IconType.Idle, this.indexedIcons(IconType.Idle, 19));
        this._icons.set(IconType.Stale, this.indexedIcons(IconType.Stale, 19));
        this._icons.set(IconType.Reallystale, this.indexedIcons(IconType.Reallystale, 19));

        this._icons.set(IconType.StoppedLabeled, this.indexedIcons(IconType.StoppedLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.IdleLabeled, this.indexedIcons(IconType.IdleLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.StaleLabeled, this.indexedIcons(IconType.StaleLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.ReallystaleLabeled, this.indexedIcons(IconType.ReallystaleLabeled, 19, this._labelAnchor, this._labelSize));

        this._icons.set(IconType.StoppedInLabeled, this.indexedIcons(IconType.StoppedInLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.IdleInLabeled, this.indexedIcons(IconType.IdleInLabeled, 19, this._labelAnchor, this._labelSize));

        this._icons.set(IconType.StoppedOutLabeled, this.indexedIcons(IconType.StoppedOutLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.IdleOutLabeled, this.indexedIcons(IconType.IdleOutLabeled, 19, this._labelAnchor, this._labelSize));

        this._icons.set(IconType.RiskLevel, this.indexedIcons(IconType.RiskLevel, 4, this._riskLevelAnchor, this._riskLevelSize));

        this._icons.set(IconType.ActiveTrafficCamNorth, this.indexedIcons(IconType.ActiveTrafficCamNorth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.ActiveTrafficCamSouth, this.indexedIcons(IconType.ActiveTrafficCamSouth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.ActiveTrafficCamEast, this.indexedIcons(IconType.ActiveTrafficCamEast, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.ActiveTrafficCamWest, this.indexedIcons(IconType.ActiveTrafficCamWest, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.ActiveTrafficCamNA, this.indexedIcons(IconType.ActiveTrafficCamNA, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.InactiveTrafficCamNorth, this.indexedIcons(IconType.InactiveTrafficCamNorth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.InactiveTrafficCamSouth, this.indexedIcons(IconType.InactiveTrafficCamSouth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.InactiveTrafficCamEast, this.indexedIcons(IconType.InactiveTrafficCamEast, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.InactiveTrafficCamWest, this.indexedIcons(IconType.InactiveTrafficCamWest, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.InactiveTrafficCamNA, this.indexedIcons(IconType.InactiveTrafficCamNA, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.VirtualCamNA, this.indexedIcons(IconType.VirtualCamNA, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.VirtualCamNorth, this.indexedIcons(IconType.VirtualCamNorth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.VirtualCamEast, this.indexedIcons(IconType.VirtualCamEast, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.VirtualCamSouth, this.indexedIcons(IconType.VirtualCamSouth, 1, this._trafficCamAnchor, this._trafficCamSize));
        this._icons.set(IconType.VirtualCamWest, this.indexedIcons(IconType.VirtualCamWest, 1, this._trafficCamAnchor, this._trafficCamSize));

        this._icons.set(IconType.Live, this.rotatedIcons(IconType.Live, 19));
        this._icons.set(IconType.Trail, this.rotatedIcons(IconType.Trail, 19));
        this._icons.set(IconType.LiveOut, this.rotatedIcons(IconType.LiveOut, 19));
        this._icons.set(IconType.LiveIn, this.rotatedIcons(IconType.LiveIn, 19));
        this._icons.set(IconType.LiveLabeled, this.rotatedIcons(IconType.LiveLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.LiveOutLabeled, this.rotatedIcons(IconType.LiveOutLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.LiveInLabeled, this.rotatedIcons(IconType.LiveInLabeled, 19, this._labelAnchor, this._labelSize));
        this._icons.set(IconType.BreadCrumb, this._crumbs);
        this._icons.set(IconType.Violation, this.indexedIcons(IconType.Violation));
        this._icons.set(IconType.UnspecifiedHeading, this.indexedIcons(IconType.UnspecifiedHeading, 19));
        this._icons.set(IconType.UnsupportedIcon, this._unsupported);
        this._icons.set(IconType.Address, this._address);
        this._icons.set(IconType.EventCollision, this._eventCollision);
        this._icons.set(IconType.EventManual, this._eventManual);
        this._icons.set(IconType.EventRecorded, this._eventRecorded);
        this._icons.set(IconType.EventRisky, this._eventRisky);
        this._icons.set(IconType.FollowingDistance, this._followingDistance);
        this._icons.set(IconType.GeofenceInEnd, this._geofenceInEnd);
        this._icons.set(IconType.GeofenceInStart, this._geofenceInStart);
        this._icons.set(IconType.GeofenceOutEnd, this._geofenceOutEnd);
        this._icons.set(IconType.GeofenceOutStart, this._geofenceOutStart);
        this._icons.set(IconType.IdleViolation, this._idleViolation);
        this._icons.set(IconType.LanedepartureMarker, this._laneDepartureMarker);
        this._icons.set(IconType.SpeedingMarker, this._speedingMarker);
        this._icons.set(IconType.TripEnd, this._tripEnd);
        this._icons.set(IconType.TripEndBlank, this._tripEndBlank);
        this._icons.set(IconType.TripEndLeft, this._tripEndLeft);
        this._icons.set(IconType.TripStart, this._tripStart);
        this._icons.set(IconType.TripStartBlank, this._tripStartBlank);
        this._icons.set(IconType.TripStartRight, this._tripStartRight);
        this._icons.set(IconType.StoppedIn, this.indexedIcons(IconType.StoppedIn, 19));
        this._icons.set(IconType.StoppedOut, this.indexedIcons(IconType.StoppedOut, 19));
        this._icons.set(IconType.IdleOut, this.indexedIcons(IconType.IdleOut, 19));
        this._icons.set(IconType.IdleIn, this.indexedIcons(IconType.IdleIn, 19));
        this._icons.set(IconType.Asset, this._asset);
        this._icons.set(IconType.AssetUnlabeled, this._assetUnlabeled);
        this._icons.set(IconType.GeofenceIcon, this._geofenceIcon);
        this._icons.set(IconType.GeofenceIconUnlabled, this._geofenceIconUnlabled);
    }

    // private initializeDistractionIcons() {
    //     this._icons.set(IconType.DistractionPhone, this._distractionPhone);
    //     this._icons.set(IconType.RollingStopAlert, this._rollingStopAlert);
    //     this._icons.set(IconType.FoodDrinkAlert, this._foodDrinkAlert);
    //     this._icons.set(IconType.UnbeltedAlert, this._unbeltedAlert);
    //     this._icons.set(IconType.SmokingAlert, this._smokingAlert);
    //     this._icons.set(IconType.ObstructionAlert, this._obstructionAlert);
    //     this._icons.set(IconType.MotionAlert, this._motionAlert);
    // }

    private initializeMarkerPlacements() {

        this._origin = new google.maps.Point(0, 0);
        console.log('chip', this.pixelCoordinates);
        this._chipAnchor = this.pixelCoordinates;
        this._chipSize = new google.maps.Size(21, 21);
        this._chipBorderAnchor = new google.maps.Point(9, 9);
        this._chipBorderSize = new google.maps.Size(17, 17);

        this._crumbAnchor = new google.maps.Point(8, 8);
        this._crumbSize = new google.maps.Size(17, 17);

        this._rightPinAnchor = new google.maps.Point(0, 37);
        this._leftPinAnchor = new google.maps.Point(29, 37);
        this._pinAnchor = new google.maps.Point(15, 37);
        this._pinSize = new google.maps.Size(29, 37);

        this._smallPinSize = new google.maps.Size(23, 29);
        this._smallPinAnchor = new google.maps.Point(0, 29);

        this._labelAnchor = new google.maps.Point(4, 11);
        this._labelSize = new google.maps.Size(101, 47);

        this._riskLevelAnchor = new google.maps.Point(11, 11);
        this._riskLevelSize = new google.maps.Size(21, 21);

        this._trafficCamAnchor = new google.maps.Point(12, 12);
        this._trafficCamSize = new google.maps.Size(24, 24);
    }

    public setMap(pixelCoordinates: google.maps.Point) {
        this.pixelCoordinates = pixelCoordinates;
        console.log(this.pixelCoordinates);
    }

    private rotatedIcons(type: IconType, colors = 1, anchor = this._chipAnchor, size = this._chipSize): google.maps.Icon[][] {

        const icons: google.maps.Icon[][] = [];

        for (let i = 0; i < colors; i++) {
            const colorArray: google.maps.Icon[] = [];
            for (let j = 0; j < RANK * RANK; j++) {

                const x = (j % RANK + i * RANK) * size.width;
                const y = Math.floor(j / RANK) * size.height;

                colorArray.push({
                    size: size,
                    anchor: anchor,
                    origin: new google.maps.Point(x, y), // row and column
                    url: this._iconMap.iconMap.get(type)
                });
            }
            icons.push(colorArray);
        }

        return icons;
    }

    private indexedIcons(type: IconType, cells = 1, anchor = this._chipAnchor, size = this._chipSize): google.maps.Icon[][] {
        const icons: google.maps.Icon[][] = [];
        icons.push([]);

        for (let i = 0; i < cells; i++) {

            const x = i * size.width;

            icons[0].push({
                size: size,
                anchor: anchor,
                origin: new google.maps.Point(x, 0), // index the next cell
                url: this._iconMap.iconMap.get(type)
            });
        }
        return icons;
    }

    get icons(): Map<IconType, google.maps.Icon[][]> {
        return this._icons;
    }

    get markerIcons(): Map<IconType, string> {
        return this._iconMap.iconMap;
    }

    get getCallOutIcon(): string {
        return this._iconMap.iconMap.get(IconType.CallOut);
    }

    private _distractionPhone: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.DistractionPhone)
    }]];

    private _rollingStopAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.RollingStopAlert)
    }]];

    private _foodDrinkAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.FoodDrinkAlert)
    }]];

    private _unbeltedAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.UnbeltedAlert)
    }]];

    private _smokingAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.SmokingAlert)
    }]];

    private _obstructionAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.ObstructionAlert)
    }]];

    private _motionAlert: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.MotionAlert)
    }]];

    private _crumbs: google.maps.Icon[][] = [[{
        size: this._crumbSize,
        anchor: this._crumbAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.BreadCrumb)
    }]];

    private _unsupported: google.maps.Icon[][] = [[{
        size: this._chipSize,
        anchor: this._chipAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.UnsupportedIcon)
    }]];

    private _address: google.maps.Icon[][] = [[{
        size: this._chipSize,
        anchor: this._chipAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.Address)
    }]];

    private _eventCollision: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.EventCollision)
    }]];

    private _eventManual: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.EventManual)
    }]];

    private _eventRecorded: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.EventRecorded)
    }]];

    private _eventRisky: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.EventRisky)
    }]];

    private _followingDistance: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.FollowingDistance)
    }]];

    private _geofenceInEnd: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.GeofenceInEnd)
    }]];

    private _geofenceInStart: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.GeofenceInStart)
    }]];

    private _geofenceOutEnd: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.GeofenceOutEnd)
    }]];

    private _geofenceOutStart: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.GeofenceOutStart)
    }]];

    private _idleViolation: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.IdleViolation)
    }]];

    private _laneDepartureMarker: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.LanedepartureMarker)
    }]];

    private _speedingMarker: google.maps.Icon[][] = [[{
        size: this._smallPinSize,
        anchor: this._smallPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.SpeedingMarker)
    }]];

    private _tripEnd: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripEnd)
    }]];

    private _tripEndBlank: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripEndBlank)
    }]];

    private _tripEndLeft: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._leftPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripEndLeft)
    }]];

    private _tripStart: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripStart)
    }]];

    private _tripStartBlank: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._pinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripStartBlank)
    }]];

    private _tripStartRight: google.maps.Icon[][] = [[{
        size: this._pinSize,
        anchor: this._rightPinAnchor,
        origin: this._origin,
        url: this._iconMap.iconMap.get(IconType.TripStartRight)
    }]];

    initializeIcons() {
        this._asset = [[{
            size: this._labelSize,
            anchor: this._labelAnchor,
            origin: this._origin,
            url: this._iconMap.iconMap.get(IconType.Asset)
        }]];
        this._assetUnlabeled = [[{
            size: this._chipSize,
            anchor: this._chipAnchor,
            origin: this._origin,
            url: this._iconMap.iconMap.get(IconType.AssetUnlabeled)
        }]];

        this._geofenceIcon = [[{
            size: this._labelSize,
            anchor: this._labelAnchor,
            origin: this._origin,
            url: this._iconMap.iconMap.get(IconType.GeofenceIcon)
        }]];

        this._geofenceIconUnlabled = [[{
            size: this._chipSize,
            anchor: this._chipAnchor,
            origin: this._origin,
            url: this._iconMap.iconMap.get(IconType.GeofenceIconUnlabled)
        }]];
    }
}




